import { RatchetingFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/ratcheting.zod.ts';
import { UseFieldArrayReturn } from 'react-hook-form';
import { RatchetMetricType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/data.mock.ts';
import { ActionIcon, TextInput, Text, useMantineTheme, Button, Flex } from '@mantine/core';
import { NumberInput } from 'react-hook-form-mantine';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	getRatchetingMetricLabel,
	getScheduleFieldName,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/useRatchetingController.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { RatchetScheduleEditStyles } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/RatchetScheduleEdit/RatchetScheduleEdit.styles.tsx';
import { SortTag } from '@dealstack/frontend/shared/components/SortTag/SortTag.tsx';

export const RatchetScheduleEdit = ({
	scheduleFieldArray,
	metricType,
	addScheduleItem,
	removeScheduleItem,
}: {
	scheduleFieldArray: UseFieldArrayReturn<RatchetingFormDto, 'schedule'>;
	metricType: RatchetMetricType;
	addScheduleItem: () => void;
	removeScheduleItem: (index: number) => void;
}) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();
	const theme = useMantineTheme();

	const isCombined = metricType === RatchetMetricType.MOIC_AND_XIRR;
	const thresholdMetrics = isCombined
		? [RatchetMetricType.MOIC, RatchetMetricType.XIRR]
		: [metricType];

	return (
		<RatchetScheduleEditStyles>
			<table>
				<thead>
					<tr>
						<th className="col-shrink" />

						{thresholdMetrics.map((metric) => (
							<th key={metric} className="col-header">
								<Text size="sm" fw={600} c="gray.9">
									{tCapManage('securities.ratcheting.schedule.threshold_header', {
										metric: getRatchetingMetricLabel(tCommonUi, metric),
									})}
								</Text>
							</th>
						))}

						<th className="col-header">
							<Text size="sm" fw={600} c="gray.9">
								{tCapManage('securities.ratcheting.schedule.entitlement_header')}
							</Text>
						</th>

						<th className="col-shrink" />
					</tr>
				</thead>

				<tbody>
					{scheduleFieldArray.fields.map((field, index) => (
						<tr key={field.id}>
							<td className="col-shrink">
								<Flex justify="center" align="center">
									<SortTag label={index} />
								</Flex>
							</td>

							<td colSpan={index === 0 ? thresholdMetrics.length : 1}>
								{index === 0 ? (
									<TextInput
										value={tCapManage(
											'securities.ratcheting.schedule.initial_placeholder'
										)}
										readOnly
										styles={{
											input: {
												marginTop: 0,
												backgroundColor: theme.colors.gray[4],
												color: theme.colors.dark[8],
											},
										}}
									/>
								) : (
									<NumberInput
										name={getScheduleFieldName(index, 'threshold')}
										placeholder="0"
										min={0}
										decimalScale={2}
										hideControls
										styles={{
											input: {
												marginTop: 0,
											},
										}}
									/>
								)}
							</td>

							{isCombined && index > 0 && (
								<td>
									<NumberInput
										name={getScheduleFieldName(index, 'xirrThreshold')}
										placeholder="0"
										min={0}
										max={100}
										decimalScale={2}
										hideControls
										styles={{
											input: {
												marginTop: 0,
											},
										}}
									/>
								</td>
							)}

							<td>
								<NumberInput
									name={getScheduleFieldName(index, 'entitlement')}
									hideControls
									min={0}
									max={100}
									placeholder="0"
									rightSection={
										<Icon
											variant="percentage"
											size="lg"
											color="black"
											style={{ paddingRight: '0.5rem' }}
										/>
									}
									styles={{
										input: {
											marginTop: 0,
										},
									}}
								/>
							</td>

							<td className="col-shrink">
								{index > 0 && scheduleFieldArray.fields.length > 2 && (
									<ActionIcon
										variant="filled"
										bg={theme.colors.gray[4]}
										size="sm"
										onClick={() => removeScheduleItem(index)}
									>
										<Icon
											variant="close"
											color="black"
											style={{ opacity: '0.7' }}
										/>
									</ActionIcon>
								)}
							</td>
						</tr>
					))}
				</tbody>

				<tfoot>
					<tr>
						<td colSpan={thresholdMetrics.length + 3}>
							<Button
								variant="default"
								leftSection={<Icon variant="add" size="lg" />}
								fullWidth
								onClick={addScheduleItem}
								styles={{
									root: { border: 0, color: 'blue' },
								}}
							>
								{tCapManage('securities.ratcheting.schedule.add_another')}
							</Button>
						</td>
					</tr>
				</tfoot>
			</table>
		</RatchetScheduleEditStyles>
	);
};
